import {
  Button,
  Container,
  Grid,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  useRef,
  startTransition,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setCart,
  DefaultStore,
  UpdateCartData,
  setUser,
  DefaultStoreFee,
  LoadDeliveryFees,
  activeMenu,
  allactiveMenues,
  menues,
  checkStore,
  userAddresses,
} from '@tabletuck/redux-slices';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';

import CloseIcon from '@mui/icons-material/Close';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import basket from '../../../assets/empty_bag.png';
import CartDrawerCard from '../Card/CartDrawerCard';
import TextField from '@mui/material/TextField';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import { setDefaultStore } from './utils';
import { fetchData, handleToaster } from '../../Utils/UtilsFunctions';
import DeliveryDialog from './DeliveryDialog';
import DailogForm from './DailogForm';
import { auth, firestore } from '@tabletuck/firebase-services';
import Snipper from './Snipper';
import BillDetails from './BillDetails';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import {
  CheckItem,
  DefaultAddress,
  setDeliveryStore,
  UdpateNote,
} from '../../../../../../libs/redux-slices/src';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { useCartPricing } from '../../hooks/useCartPricing';
import { useTranslation } from 'react-i18next';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { formatPriceFromCents } from '../../Utils/UtilsFunctions';
import { calculateDistance } from '../../services/locationService';

// Separate component for cart item to prevent unnecessary re-renders
// Memoized with specific props to avoid re-rendering when parent updates
const CartItemRow = memo(
  ({ lineItem, index, orderType, onItemRemoved, onItemUpdated }) => {
    const cart = useSelector((state) => state.shopReducer.cart);
    const [errDelivery, setErrDelivery] = useState('');

    // Find matching cart item for additional details (like image)
    const matchingCartItem = useMemo(() => {
      if (!Array.isArray(cart) || cart.length === 0) return null;
      return cart.find((cartItem) => {
        return (
          cartItem.itemId === lineItem.variationId ||
          cartItem.title?.toLowerCase() === lineItem.name?.toLowerCase() ||
          cartItem.variationId === lineItem.variationId
        );
      });
    }, [cart, lineItem]);

    // Transform lineItem data to match CartDrawerCard expectations
    const transformedItem = useMemo(() => {
      // Preserve all original lineItem data including modifiers
      return {
        // Preserve original cart item data first (for image, etc.)
        ...(matchingCartItem || {}),
        // Include original lineItem fields (this includes modifiers from API)
        // This must come after matchingCartItem to ensure API data takes priority
        ...lineItem,
        // Override with specific fields we need
        itemId:
          lineItem.variationId ||
          matchingCartItem?.itemId ||
          `line-item-${index}`,
        title: lineItem.name || matchingCartItem?.title || 'Unknown Item',
        quantity: parseInt(lineItem.quantity) || 1,
        price: lineItem.grossSalesMoney
          ? (
              parseFloat(lineItem.grossSalesMoney) /
              100 /
              parseInt(lineItem.quantity || 1)
            ).toFixed(2)
          : matchingCartItem?.price || '0.00',
        calculatedPrice: lineItem.grossSalesMoney
          ? parseFloat(lineItem.grossSalesMoney) / 100
          : matchingCartItem?.calculatedPrice || 0,
        image: matchingCartItem?.image || '',
        // CRITICAL: Ensure modifiers from lineItem (API) are preserved
        modifiers: lineItem.modifiers || [],
        // Also support modifierDetails format for backward compatibility
        modifierDetails:
          lineItem.modifiers?.map((modifier) => ({
            optionTitle: modifier.name || 'Unknown Modifier',
            price: modifier.totalPriceMoney?.amount
              ? parseFloat(modifier.totalPriceMoney.amount) / 100
              : 0,
            quantity: parseInt(modifier.quantity) || 1,
            catalogObjectId: modifier.catalogObjectId,
            uid: modifier.uid,
            basePriceMoney: modifier.basePriceMoney,
          })) ||
          matchingCartItem?.modifierDetails ||
          [],
        // Ensure variationId is available for cart operations
        variationId: lineItem.variationId || matchingCartItem?.variationId,
      };
    }, [lineItem, matchingCartItem, index]);

    return (
      <CartDrawerCard
        key={`${lineItem.variationId || 'item'}-${index}`}
        item={transformedItem}
        typeState={orderType}
        itemErr=""
        errDelivery={errDelivery}
        setErrDelivery={setErrDelivery}
        lineItemData={lineItem}
      />
    );
  }
);

const CartDrawer = ({
  handleCloseCartDialoge,
  openCartDialoge,
  userData,
  defaultStoreAdress,
  setDefaultStoreAdress,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [t, i18n] = useTranslation();

  // Anonymous auth support
  const { isAnonymous, syncCartFromFirebase } = useAnonymousAuth();

  // Unified delivery address management
  const {
    currentDeliveryAddressId: unifiedDeliveryAddressId,
    currentDeliveryAddressData,
    setDeliveryAddressById,
    setDeliveryAddressFromLocation,
    refreshAddresses,
  } = useDeliveryAddress();

  // Memoize Redux selectors to prevent unnecessary re-renders
  const note = useSelector(
    (state) => state.shopReducer.note,
    (a, b) => a === b
  );

  // Local note state prevents Redux dispatch + full re-render on every keystroke.
  // This reduces iOS/WebKit instability when the keyboard opens inside the drawer.
  const [localNote, setLocalNote] = useState(note || '');
  const noteDebounceTimeoutRef = useRef(null);
  const noteTextareaRef = useRef(null);
  const wasOpenRef = useRef(false);

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleNoteFocus = useCallback(() => {
    if (!isIOS) return;
    const el = noteTextareaRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;

    // Keep it "nearest" to reduce iOS scroll/jitter inside drawer containers.
    try {
      requestAnimationFrame(() => {
        try {
          el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } catch {
          // Ignore scroll exceptions
        }
      });
    } catch {
      // Ignore RAF exceptions
    }
  }, [isIOS]);

  // When the drawer opens, sync the text field with the latest Redux value.
  useEffect(() => {
    if (openCartDialoge) setLocalNote(note || '');
  }, [openCartDialoge, note]);

  const flushNoteDispatch = useCallback(
    (nextValue) => {
      const valueToDispatch =
        nextValue !== undefined ? nextValue : localNote;

      // Avoid redundant dispatches.
      if (valueToDispatch === note) return;

      if (noteDebounceTimeoutRef.current) {
        clearTimeout(noteDebounceTimeoutRef.current);
        noteDebounceTimeoutRef.current = null;
      }

      dispatch(UdpateNote(valueToDispatch));
    },
    [dispatch, localNote, note]
  );

  const handleCloseCartDialogeSafe = useCallback(() => {
    flushNoteDispatch();
    if (typeof handleCloseCartDialoge === 'function') {
      handleCloseCartDialoge();
    }
  }, [flushNoteDispatch, handleCloseCartDialoge]);

  // Clear note when cart becomes empty (keep local + Redux in sync).
  useEffect(() => {
    if (cart && cart.length === 0 && note) {
      if (noteDebounceTimeoutRef.current) {
        clearTimeout(noteDebounceTimeoutRef.current);
        noteDebounceTimeoutRef.current = null;
      }
      setLocalNote('');
      dispatch(UdpateNote(''));
    }
  }, [cart, note, dispatch]);

  // If the parent closes the drawer without going through our close handler,
  // flush the latest debounced note once the drawer transitions from open -> closed.
  useEffect(() => {
    if (wasOpenRef.current && !openCartDialoge) {
      flushNoteDispatch();
    }
    wasOpenRef.current = openCartDialoge;
  }, [openCartDialoge, flushNoteDispatch]);

  // Cleanup pending debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (noteDebounceTimeoutRef.current) {
        clearTimeout(noteDebounceTimeoutRef.current);
        noteDebounceTimeoutRef.current = null;
      }
    };
  }, []);

  const cart = useSelector(
    (state) => state.shopReducer.cart,
    (a, b) => a === b
  );
  const token = useSelector(
    (state) => state.authReducer.token,
    (a, b) => a === b
  );
  const stores = useSelector(
    (state) => state.shopReducer.stores,
    (a, b) => a === b
  );
  const user = useSelector(
    (state) => state.authReducer.userData,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const items = useSelector(
    (state) => state.shopReducer.currentItem,
    (a, b) => a === b
  );
  const { scheduledTime: reduxScheduledTime } = useSelector(
    (state) => state.shopReducer.cartData,
    (a, b) => a.scheduledTime === b.scheduledTime
  );
  const addresses = useSelector(
    (state) => state.authReducer.userAddresses,
    (a, b) => a === b
  );
  const deliveryStore = useSelector(
    (state) => state.shopReducer.deliveryStore,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const [err, setErr] = useState('');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [store, setStore] = useState('');
  const [open, setOpen] = useState(false);
  const [forceDeliveryDialog, setForceDeliveryDialog] = useState(false);
  const [reset, setRest] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [itemErr, setItemErr] = useState('');
  const [deliveryTooFarDialog, setDeliveryTooFarDialog] = useState(false);

  const [cartData, setCartData] = useState(cart);

  // single source of truth for current store: derive from cart items
  const cartStoreId = useMemo(() => {
    if (Array.isArray(cartData) && cartData.length > 0) {
      return cartData[0]?.storeId ?? cartData[0]?.store_id ?? '';
    }
    return '';
  }, [cartData]);

  const [dataStore, setDataStore] = useState([]);

  const [totalPrice, setTotalPrice] = useState(0);
  // const [totalItems, setTotalItems] = useState(0);

  const [isSwitchingType, setIsSwitchingType] = useState(false);
  const [defaultAdd, setDefaultAdd] = useState('');

  const [typeAddress, setTypeAddress] = useState('');
  const [checkoutErr, setCheckoutErr] = useState('');
  const [checkoutErrTotal, setcheckoutErrTotal] = useState('');

  const [storeaddress, setStoreaddress] = useState('');
  const [addressStore, setAddressStore] = useState([]);
  const [enablePickup, setEnablePickup] = useState(false);
  const [enableDelivery, setEnableDelivery] = useState(false);

  const [type, setType] = useState(() => {
    const preferred = userData?.pickupOrDelivery;
    return preferred === 'delivery' || preferred === 'pickup'
      ? preferred
      : 'pickup';
  });

  const [deliveryFee, setDeliveryFee] = useState(0);

  const [apiError, setApiError] = useState('');
  const [errDelivery, setErrDelivery] = useState('');
  const types = ['delivery', 'pickup'];
  const typeToShow = [t('confirmPage.Delivery'), `${t('confirmPage.pickUp')}`];
  const [storeData, setStoreData] = useState('');

  // Track if we've already synced cart on this drawer open
  const hasSyncedCartRef = useRef(false);

  // Helper to format store address into a US-style single-line string
  const formatAddress = (address) => {
    if (!address) return '';
    const line1 =
      address.addressLine1 || address.address_line1 || address.address1 || '';
    const line2 =
      address.addressLine2 || address.address_line2 || address.address2 || '';
    const city = address.locality || address.city || '';
    const state = address.administrativeDistrictLevel1 || address.state || '';
    const postal =
      address.postalCode ||
      address.zipCode ||
      address.postal_code ||
      address.zip ||
      '';

    const parts = [];
    if (line1) parts.push(line1);
    if (line2) parts.push(line2);

    // Combine city, state and postal into one part
    const cityState = [city, state].filter(Boolean).join(', ');
    const cityStatePostal = [cityState, postal].filter(Boolean).join(' ');
    if (cityStatePostal) parts.push(cityStatePostal);

    return parts.join(', ');
  };

  // Use the shared cart pricing hook
  const {
    apiPricingData,
    isCalculatingPrice,
    cartLoading,
    callCalculateSquareItemsPrice,
    callCalculateSquareItemsPriceImmediate,
    getCurrentDeliveryAddressId,
  } = useCartPricing({
    cart,
    cartStoreId,
    orderType: type,
    deliveryAddressId: unifiedDeliveryAddressId,
    enabled: openCartDialoge && auth?.currentUser?.uid,
  });

  const getStores = async () => {
    stores?.map((store) => {
      if (store?.id === addressStore) {
        setData(store);
      }
    });
  };

  const getAdressStore = async () => {
    stores?.map((store) => {
      if (store?.id === deliveryStore) {
        // Store found
      }
    });
  };

  const getStore = async () => {
    stores?.map((storee) => {
      if (storee?.businessName === store) {
        setDataStore(storee);
      }
    });
  };

  // Memoized display values for better performance
  const displaySubtotal = useMemo(() => {
    if (apiPricingData && apiPricingData.subtotal !== undefined) {
      return formatPriceFromCents(apiPricingData.subtotal);
    }
    return total.toFixed(2);
  }, [apiPricingData, total]);

  const displayTotal = useMemo(() => {
    if (apiPricingData && apiPricingData.total !== undefined) {
      return formatPriceFromCents(apiPricingData.total);
    }
    return total.toFixed(2);
  }, [apiPricingData, total]);

  const displayTax = useMemo(() => {
    if (apiPricingData && apiPricingData.tax !== undefined) {
      return formatPriceFromCents(apiPricingData.tax);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayModifiersPrice = useMemo(() => {
    if (apiPricingData && apiPricingData.total_modifiers_price !== undefined) {
      return formatPriceFromCents(apiPricingData.total_modifiers_price);
    }
    return '0.00';
  }, [apiPricingData]);
  const displayTotalDiscounts = useMemo(() => {
    if (apiPricingData && apiPricingData.total_discounts !== undefined) {
      return formatPriceFromCents(apiPricingData.total_discounts);
    }
    return '0.00';
  }, [apiPricingData]);
  const displayServiceCharge = useMemo(() => {
    if (apiPricingData && apiPricingData.serviceChargeMoney !== undefined) {
      return formatPriceFromCents(
        apiPricingData.serviceChargeMoney - apiPricingData.onlineFee
      );
    }
    return '0.00';
  }, [apiPricingData]);
  const displayOnlineFee = useMemo(() => {
    if (apiPricingData && apiPricingData.onlineFee !== undefined) {
      return formatPriceFromCents(apiPricingData.onlineFee);
    }
    return '0.00';
  }, [apiPricingData]);

  const getUser = async (token) => {
    try {
      const user = userData;
      const bags = user?.bagItems;
      bags?.length && dispatch(setCart(bags));
      const storeId = cartStoreId;
      setStoreId(cartStoreId);

      stores?.map((store) => {
        if (store?.id === storeId) {
          setStore(store?.businessName);
          setStoreData(store);

          setEnablePickup(store?.pickup_enabled);
          setEnableDelivery(store?.delivery_enabled);
          setStoreaddress(formatAddress(store?.address));
        }
      });
    } catch (e) {
      // Error in getUser
    }
  };
  const getUserAddresses = async (token) => {
    const bags = user?.bagItems;
    setStoreId(cartStoreId);
    bags?.length && dispatch(setCart(bags));

    try {
      // Fetch addresses from the addresses subcollection
      const addressesRef = firestore
        .collection('users')
        .doc(user?.uid)
        .collection('addresses');

      const addressesSnapshot = await addressesRef.get();
      const firestoreAddresses = [];

      addressesSnapshot.forEach((doc) => {
        firestoreAddresses.push({ id: doc.id, ...doc.data() });
      });

      // IMPORTANT: Preserve temporary addresses (from ChangeBranchMenu) when merging
      // These are addresses that exist in Redux but not in Firestore (for anonymous users)
      const existingTempAddresses = (addresses || []).filter(
        (a) => a.isTemporary
      );

      // Merge: Firestore addresses + temporary addresses
      const mergedAddresses = [...firestoreAddresses, ...existingTempAddresses];

      // Update Redux with the MERGED addresses (preserving temp addresses)
      dispatch(userAddresses(mergedAddresses));

      // IMPORTANT: Check if there's already a branchMenu source set
      // If so, don't overwrite the delivery address - branchMenu has priority
      const currentSource =
        typeof deliveryStore === 'object' ? deliveryStore?.source : null;
      const hasBranchMenuPriority = currentSource === 'branchMenu';

      if (firestoreAddresses.length > 0) {
        // Get the first Firestore address as default (not temp addresses)
        const firstAddress = firestoreAddresses[0];
        const defaultDeliveryAddress =
          user?.defaultDeliveryAddress || firstAddress.id;

        // Find the default address or use the first one (from Firestore only)
        const selectedAddress =
          firestoreAddresses.find((a) => a.id === defaultDeliveryAddress) ||
          firstAddress;

        // Update user document with defaultDeliveryAddress if not set
        if (!user?.defaultDeliveryAddress) {
          await firestore
            .collection('users')
            .doc(user?.uid)
            .update({ defaultDeliveryAddress: firstAddress.id });
        }

        // ONLY set delivery store if there's NO branchMenu priority
        // This preserves the address selected in ChangeBranchMenu
        if (!hasBranchMenuPriority) {
          setAddressStore(selectedAddress?.delivery_store_id || cartStoreId);
          dispatch(
            setDeliveryStore({
              address: selectedAddress.id,
              store: selectedAddress?.delivery_store_id || cartStoreId,
            })
          );

          // Set address display info (no delivery fee calculation here)
          setDefaultAdd(
            selectedAddress?.displayName || selectedAddress?.address
          );
          setTypeAddress(selectedAddress?.nickName || 'Default Address');
        } else {
          // Don't overwrite - the sync effect will handle displaying the correct address
        }

        setApiError('');

        // Reset delivery fee - will be calculated by calcPrice API
        setDeliveryFee(0);
        dispatch(LoadDeliveryFees(0));
      } else {
        // No addresses found
        setDefaultAdd('');
        setAddressStore('');
        setTypeAddress('');
        setApiError('');
        setDeliveryFee(0);
        dispatch(LoadDeliveryFees(0));
      }
    } catch (error) {
      setApiError(error?.message || 'Failed to load addresses');
      setDefaultAdd('');
      setDeliveryFee(0);
      dispatch(LoadDeliveryFees(0));
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    setCheckoutErr('');
  };
  const handleClose = () => {
    setOpen(false);
  };

  // Handle when a new address is added (from force delivery flow)
  // Now uses the unified useDeliveryAddress hook for consistent state management
  // Uses source: 'deliveryDialog' which has lower priority than 'branchMenu'
  const handleNewAddressAdded = useCallback(
    async (addressId) => {
      try {
        // Refresh addresses to get the newly added one
        await refreshAddresses();

        // Use the unified hook to set this as the delivery address
        // source: 'deliveryDialog' - lower priority than branchMenu
        const storeId = cartStoreId || '';
        await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');

        // Find the newly added address for local state updates
        const addressesRef = firestore
          .collection('users')
          .doc(auth?.currentUser?.uid)
          .collection('addresses');
        const addressDoc = await addressesRef.doc(addressId).get();

        if (addressDoc.exists) {
          const newAddress = { id: addressDoc.id, ...addressDoc.data() };

          // Update local state with the new address
          setDefaultAdd(newAddress?.displayName || newAddress?.address);
          setTypeAddress(newAddress?.nickName || 'Default Address');
          setAddressStore(newAddress?.delivery_store_id || cartStoreId);

          // Update user pickupOrDelivery to delivery
          const updatedUser = {
            ...userData,
            defaultDeliveryAddress: addressId,
            pickupOrDelivery: 'delivery',
          };
          dispatch(setUser({ userData: updatedUser }));

          // Persist to Firestore
          if (auth?.currentUser?.uid) {
            await firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .update({
                defaultDeliveryAddress: addressId,
                pickupOrDelivery: 'delivery',
              });
          }
        }

        // Ensure delivery type is selected
        setType('delivery');

        // Clear force delivery dialog flag and close the dialog
        setForceDeliveryDialog(false);
        setOpen(false);

        // Clear any errors
        setCheckoutErr('');
        setApiError('');

        // Call the pricing API with delivery type and the new addressId
        setTimeout(() => {
          callCalculateSquareItemsPriceImmediate('delivery', addressId);
        }, 100);
      } catch (error) {
        // Error handling new address
      }
    },
    [
      userData,
      dispatch,
      cartStoreId,
      callCalculateSquareItemsPriceImmediate,
      refreshAddresses,
      setDeliveryAddressById,
    ]
  );

  // Function to change selected delivery address
  // Now uses the unified useDeliveryAddress hook for consistent state management
  // Uses source: 'deliveryDialog' which has lower priority than 'branchMenu'
  const handleChangeAddress = useCallback(
    async (addressId) => {
      try {
        // Find the selected address from current addresses
        const selectedAddress = addresses?.find((a) => a.id === addressId);

        if (selectedAddress) {
          // Use the unified hook to set this as the delivery address
          // source: 'deliveryDialog' - lower priority than branchMenu
          const storeId = selectedAddress?.delivery_store_id || cartStoreId;
          await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');

          // Update local state
          setAddressStore(storeId);
          setDefaultAdd(
            selectedAddress?.displayName || selectedAddress?.address
          );
          setTypeAddress(selectedAddress?.nickName || 'Default Address');
          setApiError('');

          // Reset delivery fee - will be calculated by calcPrice API
          setDeliveryFee(0);
          dispatch(LoadDeliveryFees(0));

          // Close dialog
          setOpen(false);

          // Recalculate pricing with new address
          setTimeout(() => {
            callCalculateSquareItemsPriceImmediate('delivery', addressId);
          }, 100);
        }
      } catch (error) {
        setApiError('Failed to change address');
      }
    },
    [
      addresses,
      cartStoreId,
      dispatch,
      setDeliveryAddressById,
      callCalculateSquareItemsPriceImmediate,
    ]
  );
  const handleChangeType = useCallback(
    async (value) => {
      // Prevent duplicate clicks on the same type
      if (value === type) return;

      // Quick synchronous check for delivery address requirement
      // This is fast enough to not block UI
      if (value === 'delivery') {
        const hasBranchMenuAddress =
          currentDeliveryAddressData && unifiedDeliveryAddressId;
        const hasAnyAddress =
          hasBranchMenuAddress ||
          (addresses && addresses.length > 0) ||
          defaultAdd;

        if (!hasAnyAddress) {
          handleToaster('error', 'You need to add a delivery address first');
          setForceDeliveryDialog(true);
          handleClickOpen();
          return; // Don't change type if no address
        }

        // Check 5-mile delivery radius restriction
        if (currentDeliveryAddressData?.lat && currentDeliveryAddressData?.lng) {
          const storeCoords =
            storeData?._geoloc ||
            storeData?.coordinates ||
            storeData?.address;
          if (storeCoords?.lat && storeCoords?.lng) {
            const distanceKm = calculateDistance(storeCoords, currentDeliveryAddressData);
            if (distanceKm !== null && distanceKm * 0.621371 > 5) {
              setDeliveryTooFarDialog(true);
              return;
            }
          }
        }
      }

      // IMMEDIATE UI UPDATE - update type state immediately for instant feedback
      setType(value);

      // Clear errors immediately
      setCheckoutErr('');

      // Show type switching loader immediately
      setIsSwitchingType(true);

      // Call API immediately (not in Promise.resolve) so loading state shows immediately
      // Pass the deliveryId explicitly when switching to delivery
      const deliveryId =
        value === 'delivery' ? unifiedDeliveryAddressId || '' : '';
      callCalculateSquareItemsPriceImmediate(value, deliveryId).then(() => {
        setIsSwitchingType(false);
      });

      // Defer all other state updates and Redux updates to not block UI
      startTransition(() => {
        // If switching to delivery and we have a branchMenu address, use it
        if (
          value === 'delivery' &&
          currentDeliveryAddressData &&
          unifiedDeliveryAddressId &&
          !defaultAdd
        ) {
          setDefaultAdd(
            currentDeliveryAddressData?.displayName ||
              currentDeliveryAddressData?.address ||
              ''
          );
          setTypeAddress(
            currentDeliveryAddressData?.nickName || 'Default Address'
          );
          setAddressStore(
            currentDeliveryAddressData?.delivery_store_id || cartStoreId
          );
        }

        // Defer Redux updates to prevent blocking the UI
        dispatch(CheckItem(''));
        const updatedUser = { ...userData, pickupOrDelivery: value };
        dispatch(setUser({ userData: updatedUser }));
      });

      // Firebase update in complete background - don't wait
      if (auth?.currentUser?.uid) {
        Promise.resolve().then(() => {
          firestore
            .collection('users')
            .doc(auth.currentUser.uid)
            .set({ pickupOrDelivery: value }, { merge: true })
            .catch(() => {
              // Firebase update failed
            });
        });
      }
    },
    [
      type,
      addresses,
      defaultAdd,
      handleToaster,
      setForceDeliveryDialog,
      handleClickOpen,
      dispatch,
      userData,
      auth?.currentUser?.uid,
      callCalculateSquareItemsPriceImmediate,
      currentDeliveryAddressData,
      unifiedDeliveryAddressId,
      cartStoreId,
      storeData,
    ]
  );

  // Handler to navigate back to menu while preserving state (store, order type, scheduled time)
  const handleBackToMenu = useCallback(() => {
    // Get current store data from stores array
    const currentStore = stores?.find((s) => s.id === cartStoreId);

    // Close the cart drawer
    handleCloseCartDialogeSafe();

    // Navigate to menu with state to preserve context
    navigate('/menu', {
      state: {
        store: currentStore || null,
        preserveOrderType: type,
        preserveScheduledTime: reduxScheduledTime || null,
        fromCart: true,
        storeId: cartStoreId,
      },
    });
  }, [
    cartStoreId,
    store,
    type,
    reduxScheduledTime,
    unifiedDeliveryAddressId,
    userData?.defaultStore,
    stores,
    formatAddress,
    handleCloseCartDialogeSafe,
    navigate,
  ]);

  // Optimized navigation handler for better performance
  const handleNavigateToCart = useCallback(async () => {
    const hasErrors =
      (type === 'delivery' && apiError !== '') ||
      checkoutErr !== '' ||
      checkoutErrTotal !== '' ||
      errDelivery !== '';

    if (hasErrors) {
      return;
    }

    // Call calculateSquareItemsPrice API with the current delivery/pickup selection
    if (auth?.currentUser?.uid) {
      try {
        const token = await auth?.currentUser?.getIdToken();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };
        const requestBody = {
          deliveryId: type === 'delivery' ? getCurrentDeliveryAddressId() : '',
          storeId: cartStoreId || '',
          deliveryOrPickup: type || 'pickup',
          restaurantId: environment.restaurantId,
          tipAmount: 0,
          uid: auth?.currentUser?.uid,
        };
        await fetchData(
          `${environment?.apiUrl}/calculateSquareItemsPrice?restaurantId=${environment.restaurantId}`,
          'POST',
          requestBody,
          headers
        );
      } catch (apiError) {
        // Continue with navigation even if API call fails
      }
    }

    handleCloseCartDialogeSafe();

    if (
      environment.restaurantId === 'fattyPattyDev' ||
      environment.restaurantId === 'fattyPatty'
    ) {
      // Always allow navigation to confirm order page - auth check will be in ConfirmOrderPage
      if (apiPricingData) {
        dispatch(
          UpdateCartData({
            subTotal: displaySubtotal,
            typeAddress,
            orderType: type,
            storeId: cartStoreId || '',
            apiPricingData: apiPricingData,
            backendTotal: displayTotal,
            backendTax: displayTax,
            backendSubtotal: displaySubtotal,
            backendTotalDiscounts: displayTotalDiscounts,
            backendDeliveryFee: displayServiceCharge,
            scheduledTime: reduxScheduledTime, // Preserve scheduled time
          })
        );
        navigate('/confirmorder', {
          state: {
            apiPricingData: apiPricingData,
            storeId: cartStoreId || '',
            defaultAdd: defaultAdd || '',
            fromAnonymous: isAnonymous,
            deliveryId: getCurrentDeliveryAddressId(),
            storeAddressDetails: storeaddress,
          },
        });
      }
    } else {
    }
  }, [
    type,
    apiError,
    checkoutErr,
    checkoutErrTotal,
    errDelivery,
    token,
    userData?.uid,
    cartStoreId,
    handleCloseCartDialogeSafe,
    navigate,
    apiPricingData,
    isAnonymous,
  ]);

  // Optimized cart data effect - enrich cart items with storeId
  useEffect(() => {
    // Ensure each cart item contains storeId. Do NOT overwrite existing storeId set elsewhere
    const enrichedCart = Array.isArray(cart)
      ? cart.map((it) => {
          // prefer existing fields; support both storeId and store_id if present
          const existingStoreId = it?.storeId ?? it?.store_id;
          if (existingStoreId) return it;
          // If storeId missing, set to empty string — cartStoreId is authoritative
          return { ...it, storeId: '' };
        })
      : cart;

    setCartData(enrichedCart);
  }, [cart]);

  // Sync cart from Firebase when drawer opens (especially important for anonymous users)
  useEffect(() => {
    // Reset sync flag when drawer closes
    if (!openCartDialoge) {
      hasSyncedCartRef.current = false;
      return;
    }

    // Log delivery address state when drawer opens

    // Only sync once when drawer opens
    if (openCartDialoge && syncCartFromFirebase && !hasSyncedCartRef.current) {
      hasSyncedCartRef.current = true;
      syncCartFromFirebase()
        .then((result) => {
          if (result.success) {
          } else {
          }
        })
        .catch((error) => {
          // Cart sync failed
        });
    }
  }, [
    openCartDialoge,
    syncCartFromFirebase,
    unifiedDeliveryAddressId,
    currentDeliveryAddressData,
    defaultAdd,
    // Removed type from dependencies - cart sync shouldn't depend on order type
    deliveryStore,
  ]);

  // Ensure `type` follows user's preference but only adjust when store capabilities change
  // This effect now only adjusts when store capabilities change, not on user interactions
  // Removed `type` from dependencies to prevent running on every user toggle click
  useEffect(() => {
    const preferred = userData?.pickupOrDelivery;
    if (!preferred || !store) return;

    // Only adjust if current type is incompatible with store capabilities
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const needsAdjustment =
      (type === 'delivery' && !enableDelivery) ||
      (type === 'pickup' && !enablePickup);

    if (needsAdjustment) {
      const pickFirstAvailable = () => {
        if (enableDelivery) return 'delivery';
        if (enablePickup) return 'pickup';
        return 'pickup';
      };

      const finalType = pickFirstAvailable();

      setType(finalType);

      if (openCartDialoge) {
        // Use immediate call for faster response when adjusting type
        callCalculateSquareItemsPriceImmediate(finalType);
      }
    }
    // Note: type is intentionally NOT in dependencies to avoid running on user toggle clicks
    // This effect only runs when store capabilities change, not when user manually toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableDelivery,
    enablePickup,
    store,
    openCartDialoge,
    callCalculateSquareItemsPriceImmediate,
    userData?.pickupOrDelivery,
  ]);

  useEffect(() => {
    if (stores && stores?.length === 1) {
      setDefaultStore(
        token,
        stores[0].id,
        stores[0].businessName,
        setStore,
        stores[0]?.pickup_enabled,
        setEnablePickup,
        stores[0].dinein_enable,
        setEnableDineIn,
        formatAddress(stores[0].address),
        setStoreaddress,
        stores[0].address.placeId
      );
    }
  }, [stores]);
  useEffect(() => {
    if (openCartDialoge) {
      getStore();
    }
  }, [store, openCartDialoge]); // Removed setStore - setter functions are stable and don't need to be in deps

  useEffect(() => {
    if (openCartDialoge) {
      getStores();
    }
  }, [addressStore, openCartDialoge]);

  useEffect(() => {
    if (openCartDialoge) {
      getAdressStore();
    }
  }, [deliveryStore, openCartDialoge]);
  // useEffect(() => {
  //   userData && getUser(auth?.currentUser?.uid);
  // }, [userData, store]);
  // useEffect(() => {
  //   auth?.currentUser?.uid && getUserAddresses(auth?.currentUser?.uid);
  // }, [auth?.currentUser?.uid]);

  useEffect(() => {
    // Skip if drawer is closed - major performance optimization
    if (!openCartDialoge) return;

    // Defer these heavy operations to not block UI
    startTransition(() => {
      userData && getUser(auth?.currentUser?.uid);
      auth?.currentUser?.uid && getUserAddresses(auth?.currentUser?.uid);
    });
  }, [userData, store, openCartDialoge]);

  // Memoized cart calculations for better performance
  const cartCalculations = useMemo(() => {
    let items = 0;
    let price = 0;
    let total = 0;

    if (cartData && Array.isArray(cartData)) {
      cartData.forEach((item) => {
        // Validate and sanitize values to prevent NaN
        const itemQty = item.quantity || item.qty || 0;
        const itemPrice = item.price && !isNaN(item.price) ? item.price : 0;
        const itemCalculatedPrice =
          item.calculatedPrice && !isNaN(item.calculatedPrice)
            ? item.calculatedPrice
            : 0;

        if (!isNaN(itemQty)) {
          items += itemQty;
          price += itemQty * itemPrice;
          total += itemCalculatedPrice;
        }
      });
    }

    return {
      items: isNaN(items) ? 0 : items,
      price: isNaN(price) ? 0 : price,
      total: isNaN(total) ? 0 : total,
    };
  }, [cartData]);

  // Update state when calculations change
  // useEffect(() => {
  //   setTotalItems(cartCalculations.items);
  //   setTotalPrice(cartCalculations.price);
  //   setTotal(cartCalculations.total);
  // }, [cartCalculations]);

  useEffect(() => {
    setcheckoutErrTotal(``);
  }, [total]);

  // SYNC: Update local state when delivery address changes from another component (e.g., ChangeBranchMenu)
  // This ensures CartDrawer always shows the current delivery address
  useEffect(() => {
    if (currentDeliveryAddressData && type === 'delivery') {
      const newDisplayName =
        currentDeliveryAddressData.displayName ||
        currentDeliveryAddressData.address ||
        '';

      setDefaultAdd(newDisplayName);
      setTypeAddress(currentDeliveryAddressData.nickName || 'Default Address');
      setAddressStore(
        currentDeliveryAddressData.delivery_store_id || cartStoreId
      );
      setApiError('');
    }
  }, [
    currentDeliveryAddressData,
    type,
    cartStoreId,
    defaultAdd,
    unifiedDeliveryAddressId,
  ]);

  // Note: API calls are now handled by useCartPricing hook automatically
  // when cart, cartStoreId, orderType, or deliveryAddressId changes

  const newCache = useMemo(
    () =>
      createCache({
        key: 'my-prefix-key',
      }),
    []
  );

  const cacheRtl = useMemo(
    () =>
      createCache({
        key: 'muirtl',
        stylisPlugins:
          i18n.language === 'ar' || i18n.language === 'he'
            ? [prefixer, rtlPlugin]
            : [prefixer],
      }),
    [i18n.language]
  );

  return (
    <CacheProvider value={newCache}>
      <Drawer
        variant="persistent"
        anchor={i18n.language === 'ar' ? 'left' : 'right'}
        open={openCartDialoge}
        onClose={() => {
          if (typeof handleCloseCartDialoge === 'function')
            handleCloseCartDialogeSafe();
        }}
        sx={{
          zIndex: 1400,
        }}
      >
        {(type === 'delivery' || forceDeliveryDialog) && open && (
          <DeliveryDialog
            open={open}
            handleDefault={setDefaultAdd}
              handleCloseCartDialoge={handleCloseCartDialogeSafe}
            token={user?.uid}
            setErr={setErr}
            handleClose={() => {
              handleClose();
              setForceDeliveryDialog(false);
            }}
            items={addresses}
            setAddressStore={setAddressStore}
            handleFee={setDeliveryFee}
            setApiError={setApiError}
            onAddressChange={
              forceDeliveryDialog ? handleNewAddressAdded : handleChangeAddress
            }
          />
        )}
        {type === 'pickup' && open && (
          <DailogForm
            open={open}
            handleClose={handleClose}
            onClickItem={setStore}
            type={type}
            enablePickup={setEnablePickup}
            enableDelivery={setEnableDelivery}
            setErr={setErr}
            onClickAdress={setStoreaddress}
            setDefaultStoreAdress={setDefaultStoreAdress}
            defaultStoreAdress={defaultStoreAdress}
          />
        )}
        <Dialog
          open={deliveryTooFarDialog}
          onClose={() => setDeliveryTooFarDialog(false)}
          sx={{ zIndex: 1500 }}
        >
          <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700 }}>
            Delivery Not Available
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: 'Jost, sans-serif', color: '#172B4D' }}>
              Delivery is not available for this address. Your delivery address
              is more than 5 miles from the store. If you want to continue,
              please choose another address.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '16px', gap: 1 }}>
            <Button
              onClick={() => {
                setDeliveryTooFarDialog(false);
                setForceDeliveryDialog(true);
                handleClickOpen();
              }}
              variant="contained"
              disableRipple
              sx={{
                backgroundColor: environment.mainColor,
                color: 'white',
                borderRadius: '43px',
                fontFamily: 'Jost',
                textTransform: 'capitalize',
                boxShadow: 'none',
                '&:hover': { backgroundColor: environment.mainColor, boxShadow: 'none' },
              }}
            >
              Choose Another Address
            </Button>
            <Button
              onClick={() => setDeliveryTooFarDialog(false)}
              variant="outlined"
              disableRipple
              sx={{
                borderRadius: '43px',
                fontFamily: 'Jost',
                textTransform: 'capitalize',
                borderColor: '#E6E6E6',
                color: '#172B4D',
                '&:hover': { borderColor: '#E6E6E6', backgroundColor: '#f5f5f5' },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: '100%', lg: '500px' },
            padding: '10px',
            maxHeight: '100dvh',
            overflowY: 'auto',
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: '15px 20px', marginBottom: '10px' }}
          >
            <Grid item>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#172B4D',
                  fontWeight: '700',
                  fontSize: '20px',
                  textTransform: 'none',
                }}
              >
                Shopping Cart ({apiPricingData?.lineItemData?.length || 0})
              </Typography>
            </Grid>
            <Grid item>
              <CloseIcon
                sx={{
                  color: '#1A1A1A',
                  cursor: 'pointer',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
                onClick={() => {
                  if (typeof handleCloseCartDialogeSafe === 'function')
                    handleCloseCartDialogeSafe();
                }}
              />
            </Grid>
          </Grid>

          {auth?.currentUser && cartData?.length > 0 && (
            <>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  padding: '20px 5%',
                  backgroundColor: 'transparent',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '50px',
                    padding: '4px',
                    width: '100%',
                    maxWidth: '400px',
                  }}
                >
                  {types?.map((value, index) =>
                    (store?.length > 0 &&
                      !enableDelivery &&
                      value === 'delivery') ||
                    (store?.length > 0 &&
                      !enablePickup &&
                      value === 'pickup') ? null : (
                      <Button
                        key={value || index}
                        disableRipple={true}
                        disableTouchRipple={true}
                        disableFocusRipple={true}
                        sx={{
                          flex: 1,
                          margin: '0 2px',
                          backgroundColor:
                            type === value
                              ? environment.mainColor
                              : 'transparent',
                          textShadow:
                            type === value
                              ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                              : 'none',
                          color: type === value ? 'white' : '#8E8E93',
                          borderRadius: '46px',
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: '600',
                          fontSize: '14px',
                          textTransform: 'capitalize',
                          padding: '4px',
                          minHeight: '44px',
                          boxShadow: 'none',
                          border: 'none',
                          transition: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          '&:hover': {
                            backgroundColor:
                              type === value
                                ? environment.mainColor
                                : 'transparent',
                            boxShadow: 'none',
                          },
                          '&:active': {
                            backgroundColor:
                              type === value
                                ? environment.mainColor
                                : 'transparent',
                            boxShadow: 'none',
                          },
                          '&.MuiButton-contained': {
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: 'none',
                            },
                          },
                        }}
                        disabled={
                          store?.length > 0 &&
                          !enablePickup &&
                          value === 'pickup'
                        }
                        variant={type === value ? 'contained' : 'text'}
                        onClick={() => { console.log('[CartDrawer BUTTON CLICK] value:', value); handleChangeType(value); }}
                      >
                        {typeToShow[index]}
                      </Button>
                    )
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  padding: '15px 5%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {/* Icon Section */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#FFF4F0',
                    marginBottom: '8px',
                  }}
                >
                  {type === 'delivery' ? (
                    !apiError ? (
                      <LocalShippingIcon
                        style={{
                          color: environment.mainColor,
                          fontSize: '28px',
                        }}
                      />
                    ) : (
                      <ReportGmailerrorredIcon
                        style={{
                          color: 'red',
                          fontSize: '28px',
                        }}
                      />
                    )
                  ) : (
                    <LocalShippingIcon
                      style={{
                        color: environment.mainColor,
                        fontSize: '28px',
                      }}
                    />
                  )}
                </Box>

                {/* Address/Store Text */}
                <Box sx={{ textAlign: 'center' }}>
                  {type === 'delivery' ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: '#172B4D',
                        fontWeight: '600',
                        fontSize: '16px',
                        lineHeight: '1.4',
                        maxWidth: '280px',
                      }}
                    >
                      {apiError
                        ? t('basket.addressOutService')
                        : defaultAdd || err}
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          color: '#172B4D',
                          fontWeight: '600',
                          fontSize: '16px',
                        }}
                      >
                        {store}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          color: '#172B4D',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        {storeaddress}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {/* Change Button */}
                {type === 'delivery' && (
                  <Stack spacing={0.5}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: '#172B4D',
                        fontWeight: '600',
                        fontSize: '16px',
                      }}
                    >
                      {store}
                    </Typography>
                    <Typography
                      component="button"
                      onClick={() => {
                        handleClickOpen(true);
                        setCheckoutErr('');
                      }}
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: environment.mainColor,
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        border: 'none',

                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {defaultAdd
                        ? `${t('basket.changeAddress')}`
                        : `${t('basket.changeAddress')}`}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </>
          )}

          <CacheProvider value={cacheRtl}>
            <Box
              sx={{
                width: '100%',
                padding: '10px 10%',
                display: 'contents',
              }}
            >
              <div style={{ paddingTop: '50px', display: 'contents' }}>
                {/* Show loading first if cart exists but no data yet, or if explicitly loading */}
                {cartLoading ||
                (cartData?.length > 0 && !apiPricingData?.lineItemData) ? (
                  <Container
                    maxWidth="lg"
                    sx={{
                      height: '60vh',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <CircularProgress
                      size={40}
                      sx={{ color: environment.mainColor }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: '#666',
                        textAlign: 'center',
                      }}
                    >
                      {isSwitchingType
                        ? 'Updating pricing...'
                        : 'Loading your cart...'}
                    </Typography>
                  </Container>
                ) : apiPricingData?.lineItemData &&
                  apiPricingData?.lineItemData?.length > 0 ? (
                  // Use optimized CartItemRow component for better performance
                  apiPricingData.lineItemData.map((lineItem, indx) => (
                    <CartItemRow
                      key={`${lineItem.variationId || 'item'}-${indx}`}
                      lineItem={lineItem}
                      index={indx}
                      orderType={type}
                      onItemRemoved={() => {
                        // Optional: Add callback if needed
                      }}
                      onItemUpdated={() => {
                        // Trigger price recalculation if needed
                        // This is now handled automatically by useCartPricing hook
                      }}
                    />
                  ))
                ) : (
                  <Container
                    maxWidth="lg"
                    sx={{
                      height: '80vh',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Grid
                      container
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingBottom: '30px',
                        textAlign: 'center',
                      }}
                    >
                      <Grid
                        item
                        lg={12}
                        style={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <img src={basket} alt="emptybag" />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingBottom: '30px',
                      }}
                    >
                      <div>
                        {' '}
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                          {t('basket.emptyCart')}
                        </Typography>
                        <br />
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 'bold', color: 'grey' }}
                        >
                          {t('basket.goOnStockOrder')}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid
                      container
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Button
                        variant="contained"
                        disableRipple
                        sx={{
                          textTransform: 'capitalize',
                          padding: '16px 40px',
                          width: '320px',
                          boxShadow: 'none',
                          fontWeight: 600,
                          borderRadius: '43px',
                          backgroundColor: environment.mainColor,
                          fontFamily: 'Jost',
                          '&:hover': {
                            backgroundColor: environment.mainColor,
                            boxShadow: 'none',
                          },
                        }}
                        onClick={handleBackToMenu}
                      >
                        {t('basket.addItems')}
                      </Button>
                    </Grid>
                  </Container>
                )}
              </div>

              {!!apiPricingData?.lineItemData &&
                !!apiPricingData?.lineItemData?.length &&
                apiPricingData?.lineItemData?.length > 0 && (
                  <>
                    <Box
                      sx={{
                        width: '100%',
                        padding: '12px 5%',
                      }}
                    >
                      <TextField
                        id="standard-basic"
                        label={t('basket.addANote')}
                        variant="outlined"
                        multiline
                        rows={2.5}
                        value={localNote || ''}
                        inputRef={noteTextareaRef}
                        onFocus={handleNoteFocus}
                        sx={{
                          width: '100%',
                          marginTop: '10px',

                          '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            '& fieldset': {
                              borderColor: '#E6E6E6',
                            },
                            '&:hover fieldset': {
                              borderColor: '#E6E6E6',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: environment.mainColor,
                            },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Jost, sans-serif',
                            color: '#B0B0B0',
                            fontSize: '15px',
                          },
                          '& .MuiInputLabel-root': {
                            fontFamily: 'Jost, sans-serif',
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: environment.mainColor,
                          },
                        }}
                        placeholder={t('basket.addANote')}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setLocalNote(nextValue);

                          // Debounce Redux updates: avoids rapid re-rendering on iOS while typing.
                          if (noteDebounceTimeoutRef.current) {
                            clearTimeout(noteDebounceTimeoutRef.current);
                          }

                          noteDebounceTimeoutRef.current = setTimeout(() => {
                            dispatch(UdpateNote(nextValue));
                            noteDebounceTimeoutRef.current = null;
                          }, 400);
                        }}
                        onBlur={() => flushNoteDispatch()}
                      />
                    </Box>
                    {/* Totals and messages (non-sticky) */}
                    <Box sx={{ padding: '16px 5%' }}>
                      <BillDetails
                        type={type || 'pickup'}
                        data={data}
                        total={total}
                        addressStore={addressStore}
                        store={store}
                        dataStore={dataStore}
                        deliveryFee={displayServiceCharge}
                        backendTax={displayTax}
                        displayTotal={displayTotal}
                        displaySubtotal={displaySubtotal}
                        displayModifiersPrice={displayModifiersPrice}
                        isCalculatingPrice={isCalculatingPrice}
                        displayDeliveryFee={displayServiceCharge}
                        totalDiscounts={displayTotalDiscounts}
                        storeData={storeData}
                        taxesText={apiPricingData?.taxes_text}
                        displayOnlineFee={displayOnlineFee}
                      />
                      <Typography
                        component="a"
                        variant="body1"
                        color={environment.mainColor}
                        sx={{
                          fontFamily:
                            '"Roboto", "Helvetica", "Arial", sans-serif',
                          color: environment.mainColor,
                          fontWeight: 700,
                          display: 'flex',
                          marginTop: '10px',
                          justifyContent: 'center',
                        }}
                      >
                        {checkoutErr}
                        <br />
                        {checkoutErrTotal}
                      </Typography>
                    </Box>
                    {/* Sticky proceed button only */}
                    <Box
                      sx={{
                        position: 'sticky',
                        bottom: 0,
                        // backgroundColor: 'white',
                        zIndex: 10,
                        // borderTop: '1px solid #E0E0E0',
                        // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                        padding: '16px 5%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                      }}
                    >
                      {/* Add More Items Button */}
                      <Button
                        variant="outlined"
                        onClick={handleBackToMenu}
                        disableRipple
                        sx={{
                          textTransform: 'capitalize',
                          padding: '14px 40px',
                          width: '100%',
                          boxShadow: 'none',
                          fontWeight: 600,
                          borderRadius: '43px',
                          backgroundColor: 'white',
                          color: environment.mainColor,
                          border: `2px solid ${environment.mainColor}`,
                          fontFamily: 'Jost',
                          '&:hover': {
                            backgroundColor: '#FFF4F0',
                            border: `2px solid ${environment.mainColor}`,
                            boxShadow: 'none',
                          },
                        }}
                      >
                        + Add More Items
                      </Button>

                      {/* Proceed to Checkout Button */}
                      <Button
                        variant="contained"
                        disabled={
                          (type === 'delivery' && apiError !== '') ||
                          checkoutErr !== '' ||
                          checkoutErrTotal !== '' ||
                          errDelivery !== ''
                        }
                        onClick={handleNavigateToCart}
                        disableRipple
                        sx={{
                          textTransform: 'capitalize',
                          padding: '16px 40px',
                          width: '100%',
                          boxShadow: 'none',
                          fontWeight: 600,
                          borderRadius: '43px',
                          backgroundColor: environment.mainColor,
                          textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                          fontFamily: 'Jost',
                          '&:hover': {
                            backgroundColor: environment.mainColor,
                            boxShadow: 'none',
                          },
                          '&:disabled': {
                            backgroundColor: '#ccc',
                            color: '#999',
                          },
                        }}
                      >
                        {t('basket.procedToCheckout')}
                      </Button>
                    </Box>
                  </>
                )}
            </Box>
          </CacheProvider>
        </Box>
      </Drawer>
    </CacheProvider>
  );
};
export default memo(CartDrawer);
